import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useFetchSingle } from '../hooks/useFetchSingle'
import { PluginData } from '../types/PluginData'
import i18n from '../i18n'
import { ApiRoutes } from '../services/ApiService'
import { toSnakeCase } from '../helpers/stringHelper'

type PluginContextType = {
    plugin: PluginData | null
    plugin_id: string | undefined
}

const PluginContext = createContext<PluginContextType | undefined>(undefined)

type PluginContextProps = {
    children: React.ReactNode | React.ReactNode[]
}

export const PluginContextProvider = ({
    children,
}: PluginContextProps): JSX.Element => {
    const { data: plugin, fetch: fetchPlugin } = useFetchSingle<PluginData>(
        ApiRoutes.PLUGIN
    )

    const plugin_id = useMemo(
        () => process.env.REACT_APP_PLUGIN_ID ?? 'plugin',
        []
    )

    const languageKey = useMemo(
        () => `${toSnakeCase(plugin_id)}_language`,
        [plugin_id]
    )

    useEffect(() => {
        fetchPlugin(
            window.location.href.includes('admin') ? 'admin' : 'frontend'
        )
    }, [fetchPlugin])

    useEffect(() => {
        if (!plugin) return
        i18n.changeLanguage(plugin.language.i18n).then(() => {
            localStorage.setItem(languageKey, plugin.language.i18n)
        })
    }, [plugin])

    return (
        <PluginContext.Provider
            value={{
                plugin,
                plugin_id,
            }}
        >
            {children}
        </PluginContext.Provider>
    )
}

export const usePluginContext = () => {
    const data = useContext(PluginContext)

    if (data === undefined) {
        throw new Error(
            'usePluginContext must used within PluginContextProvider'
        )
    }

    return data
}
